import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
	useCostCenters,
	useMillSizes,
	useRollApplications,
	useRollMaterials,
	useSuppliers,
} from "../hooks/useRollshopOperations";
import type { RollListItem } from "../models/RollshopOperations";
import type { RollUpsertPayload } from "../repositories/RollshopOperationsRepository";

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

// Slice 'YYYY-MM-DD HH:MM:SS' / 'YYYY-MM-DDTHH:MM:SS' to 'YYYY-MM-DD' for the
// HTML date input. The SP returns a string already; we don't need to parse.
const toDateInputValue = (raw: string | null | undefined): string => {
	if (!raw) return "";
	return String(raw).slice(0, 10);
};

const parseFloatOrNull = (v: string): number | null => {
	const t = v.trim();
	if (!t) return null;
	const n = Number(t);
	return Number.isFinite(n) ? n : null;
};

const parseIntOrNull = (v: string): number | null => {
	const t = v.trim();
	if (!t) return null;
	const n = Number.parseInt(t, 10);
	return Number.isFinite(n) ? n : null;
};

interface Props {
	roll: RollListItem | null;
	isSaving: boolean;
	disabled?: boolean;
	disabledReason?: string;
	onSave: (payload: RollUpsertPayload) => void;
	onClose: () => void;
}

// Detail panel with the autosave-on-blur pattern + the project-wide
// master-detail form standard (see feedback_master_detail_form_standard).
//
// - Existing roll (Id > 0): no SAVE button. Each TextField saves on blur if
//   the value actually changed; each Select / Date saves on change. Errors
//   short-circuit the save (e.g. empty Name).
// - New roll (Id null/0): no autosave. CREATE button is shown until the
//   record gets an Id. Then it switches to autosave mode automatically.
export const RollDetailPanel = ({
	roll,
	isSaving,
	disabled = false,
	disabledReason,
	onSave,
	onClose,
}: Props) => {
	const millSizesQuery = useMillSizes();
	const applicationsQuery = useRollApplications();
	const materialsQuery = useRollMaterials();
	const costCentersQuery = useCostCenters();
	const suppliersQuery = useSuppliers();

	const isNew = roll != null && (roll.Id == null || roll.Id <= 0);
	const isReadOnly = disabled || isSaving;

	// Local form state — initialised from the selected roll, reset whenever
	// the selected row id changes.
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [millSizeId, setMillSizeId] = useState<number | null>(null);
	const [applicationId, setApplicationId] = useState<number | null>(null);
	const [materialId, setMaterialId] = useState<number | null>(null);
	const [costCenterId, setCostCenterId] = useState<number | null>(null);
	const [supplierId, setSupplierId] = useState<number | null>(null);
	const [diameter, setDiameter] = useState<string>("");
	const [blankDiameter, setBlankDiameter] = useState<string>("");
	const [hardness, setHardness] = useState<string>("");
	const [cost, setCost] = useState<string>("");
	const [orderTimestamp, setOrderTimestamp] = useState("");
	const [receiveTimestamp, setReceiveTimestamp] = useState("");
	const [invoiceTimestamp, setInvoiceTimestamp] = useState("");
	const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");

	// Repopulate ONLY when a different roll is selected (Id changes). A
	// background refetch that returns the same Id with refreshed data
	// (post-autosave) must NOT clobber the values the user is typing in.
	// biome-ignore lint/correctness/useExhaustiveDependencies: deliberately
	// keyed on `roll?.Id` to avoid resetting form on refetch.
	useEffect(() => {
		if (!roll) return;
		setName(roll.Name ?? "");
		setDescription(roll.Description ?? "");
		setMillSizeId(roll.MillSizeId);
		setApplicationId(roll.RollApplicationId);
		setMaterialId(roll.RollMaterialId);
		setCostCenterId(roll.CostCenterId);
		setSupplierId(roll.SupplierId);
		setDiameter(roll.Diameter == null ? "" : roll.Diameter.toFixed(2));
		setBlankDiameter(
			roll.BlankDiameter == null ? "" : roll.BlankDiameter.toFixed(2),
		);
		setHardness(roll.Hardness == null ? "" : String(roll.Hardness));
		setCost(roll.Cost == null ? "" : String(roll.Cost));
		setOrderTimestamp(toDateInputValue(roll.OrderTimestamp));
		setReceiveTimestamp(toDateInputValue(roll.ReceiveTimestamp));
		setInvoiceTimestamp(toDateInputValue(roll.InvoiceTimestamp));
		setSupplierInvoiceNumber(roll.SupplierInvoiceNumber ?? "");
	}, [roll?.Id]);

	// Block editing the diameter on a roll that has already been turned —
	// matches the legacy `wasTurned` editor guard.
	const diameterDisabled = isReadOnly || (!isNew && !!roll?.WasTurned);

	const nameError = useMemo(() => {
		if (!name?.trim()) return "Name is required";
		if (name.trim().length > 12) return "Max 12 characters";
		return "";
	}, [name]);

	const canCreate =
		!isReadOnly && !nameError && roll != null && name.trim().length > 0;

	const buildPayload = (
		patch?: Partial<RollUpsertPayload>,
	): RollUpsertPayload => ({
		Id: roll?.Id && roll.Id > 0 ? roll.Id : null,
		Name: name.trim(),
		Description: description.trim() || null,
		RollApplicationId: applicationId,
		BlankDiameter: parseFloatOrNull(blankDiameter),
		Diameter: parseFloatOrNull(diameter),
		Hardness: parseIntOrNull(hardness),
		RollMaterialId: materialId,
		MillSizeId: millSizeId,
		Cost: parseFloatOrNull(cost),
		CostCenterId: costCenterId,
		OrderTimestamp: orderTimestamp || null,
		ReceiveTimestamp: receiveTimestamp || null,
		InvoiceTimestamp: invoiceTimestamp || null,
		PurchaseOrderItemId: roll?.PurchaseOrderItemId ?? null,
		SupplierId: supplierId,
		SupplierInvoiceNumber: supplierInvoiceNumber.trim() || null,
		...patch,
	});

	const autosaveText = (
		field: keyof RollUpsertPayload,
		newValue: string | number | null,
	) => {
		if (isReadOnly || isNew || !roll) return;
		if (nameError) return;
		const original = (roll as unknown as Record<string, unknown>)[
			field as string
		];
		const normOrig = original == null || original === "" ? null : original;
		const normNew = newValue == null || newValue === "" ? null : newValue;
		if (normOrig === normNew) return;
		onSave(buildPayload({ [field]: newValue } as Partial<RollUpsertPayload>));
	};

	const autosaveSelect = (
		field: keyof RollUpsertPayload,
		newValue: number | string | null,
	) => {
		if (isReadOnly || isNew || !roll) return;
		if (nameError) return;
		const original = (roll as unknown as Record<string, unknown>)[
			field as string
		];
		const normOrig = original == null ? null : original;
		const normNew = newValue == null ? null : newValue;
		if (normOrig === normNew) return;
		onSave(buildPayload({ [field]: newValue } as Partial<RollUpsertPayload>));
	};

	const handleCreate = () => {
		if (!canCreate || !roll) return;
		onSave(buildPayload());
	};

	if (!roll) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 3,
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">
					Select a roll to view details, or click "Add" to create a new one.
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNew ? "New Roll" : (roll.Name ?? "")}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{isNew && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					<Tooltip title="Close">
						<span>
							<IconButton size="small" onClick={onClose} disabled={isSaving}>
								<CloseIcon />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			{/* When `disabled`, inputs are greyed out and the reason follows the
			    cursor as a tooltip instead of a banner that bloats the panel. */}
			<Tooltip
				title={disabled ? disabledReason : ""}
				followCursor
				disableHoverListener={!disabled}
			>
				<Grid2 container spacing={1}>
					{/* Identification */}
					<Grid2 size={{ xs: 12 }}>
						<Typography sx={sectionHeaderSx}>Identification</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<TextField
							label="Name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							onBlur={(e) => autosaveText("Name", e.target.value.trim())}
							fullWidth
							size="small"
							required
							inputProps={{ maxLength: 12 }}
							disabled={isReadOnly}
							error={!!nameError}
							helperText={nameError}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<TextField
							label="Description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							onBlur={(e) =>
								autosaveText("Description", e.target.value.trim() || null)
							}
							fullWidth
							size="small"
							inputProps={{ maxLength: 200 }}
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<TextField
							select
							label="Mill Size"
							value={millSizeId == null ? "" : String(millSizeId)}
							onChange={(e) => {
								const v = e.target.value === "" ? null : Number(e.target.value);
								setMillSizeId(v);
								autosaveSelect("MillSizeId", v);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						>
							<MenuItem value="">—</MenuItem>
							{(millSizesQuery.data ?? []).map((m) => (
								<MenuItem key={m.Id} value={String(m.Id)}>
									{m.Name}
								</MenuItem>
							))}
						</TextField>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<TextField
							select
							label="Roll Application"
							value={applicationId == null ? "" : String(applicationId)}
							onChange={(e) => {
								const v = e.target.value === "" ? null : Number(e.target.value);
								setApplicationId(v);
								autosaveSelect("RollApplicationId", v);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						>
							<MenuItem value="">—</MenuItem>
							{(applicationsQuery.data ?? []).map((m) => (
								<MenuItem key={m.Id} value={String(m.Id)}>
									{m.Name}
								</MenuItem>
							))}
						</TextField>
					</Grid2>

					{/* Specs */}
					<Grid2 size={{ xs: 12 }}>
						<Typography sx={sectionHeaderSx}>Specs</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Diameter"
							type="number"
							value={diameter}
							onChange={(e) => setDiameter(e.target.value)}
							onBlur={(e) =>
								autosaveText("Diameter", parseFloatOrNull(e.target.value))
							}
							fullWidth
							size="small"
							disabled={diameterDisabled}
							helperText={
								!isNew && roll?.WasTurned
									? "Cannot edit — roll already turned"
									: undefined
							}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Blank Diameter"
							type="number"
							value={blankDiameter}
							onChange={(e) => setBlankDiameter(e.target.value)}
							onBlur={(e) =>
								autosaveText("BlankDiameter", parseFloatOrNull(e.target.value))
							}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Hardness"
							type="number"
							value={hardness}
							onChange={(e) => setHardness(e.target.value)}
							onBlur={(e) =>
								autosaveText("Hardness", parseIntOrNull(e.target.value))
							}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							select
							label="Roll Material"
							value={materialId == null ? "" : String(materialId)}
							onChange={(e) => {
								const v = e.target.value === "" ? null : Number(e.target.value);
								setMaterialId(v);
								autosaveSelect("RollMaterialId", v);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						>
							<MenuItem value="">—</MenuItem>
							{(materialsQuery.data ?? []).map((m) => (
								<MenuItem key={m.Id} value={String(m.Id)}>
									{m.Name}
								</MenuItem>
							))}
						</TextField>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Cost"
							type="number"
							value={cost}
							onChange={(e) => setCost(e.target.value)}
							onBlur={(e) =>
								autosaveText("Cost", parseFloatOrNull(e.target.value))
							}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							select
							label="G/L Cost Center"
							value={costCenterId == null ? "" : String(costCenterId)}
							onChange={(e) => {
								const v = e.target.value === "" ? null : Number(e.target.value);
								setCostCenterId(v);
								autosaveSelect("CostCenterId", v);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						>
							<MenuItem value="">—</MenuItem>
							{(costCentersQuery.data ?? []).map((m) => (
								<MenuItem key={m.Id} value={String(m.Id)}>
									{m.Name}
								</MenuItem>
							))}
						</TextField>
					</Grid2>

					{/* Order & Supplier */}
					<Grid2 size={{ xs: 12 }}>
						<Typography sx={sectionHeaderSx}>Order &amp; Supplier</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Order Date"
							type="date"
							value={orderTimestamp}
							onChange={(e) => {
								setOrderTimestamp(e.target.value);
								autosaveSelect("OrderTimestamp", e.target.value || null);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Receive Date"
							type="date"
							value={receiveTimestamp}
							onChange={(e) => {
								setReceiveTimestamp(e.target.value);
								autosaveSelect("ReceiveTimestamp", e.target.value || null);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Invoice Date"
							type="date"
							value={invoiceTimestamp}
							onChange={(e) => {
								setInvoiceTimestamp(e.target.value);
								autosaveSelect("InvoiceTimestamp", e.target.value || null);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 8 }}>
						<TextField
							select
							label="Supplier"
							value={supplierId == null ? "" : String(supplierId)}
							onChange={(e) => {
								const v = e.target.value === "" ? null : Number(e.target.value);
								setSupplierId(v);
								autosaveSelect("SupplierId", v);
							}}
							fullWidth
							size="small"
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						>
							<MenuItem value="">—</MenuItem>
							{(suppliersQuery.data ?? []).map((m) => (
								<MenuItem key={m.Id} value={String(m.Id)}>
									{m.Name}
								</MenuItem>
							))}
						</TextField>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<TextField
							label="Supplier Invoice No."
							value={supplierInvoiceNumber}
							onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
							onBlur={(e) =>
								autosaveText(
									"SupplierInvoiceNumber",
									e.target.value.trim() || null,
								)
							}
							fullWidth
							size="small"
							inputProps={{ maxLength: 50 }}
							disabled={isReadOnly}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>

					{!isNew && (
						<>
							{/* Status (read-only) */}
							<Grid2 size={{ xs: 12 }}>
								<Typography sx={sectionHeaderSx}>Status (read-only)</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<TextField
									label="Status"
									value={roll.StatusName ?? roll.StatusCode ?? ""}
									fullWidth
									size="small"
									disabled
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<TextField
									label="Order Status"
									value={roll.OrderStatusName ?? ""}
									fullWidth
									size="small"
									disabled
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<TextField
									label="Returned"
									value={roll.ReturnedDateString ?? ""}
									fullWidth
									size="small"
									disabled
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>
						</>
					)}
				</Grid2>
			</Tooltip>
		</Box>
	);
};
